import { useState } from "react";
import { ActionIcon, Badge, Button, Checkbox, Group, Menu, Modal, Stack, Table, Tabs, Text, Textarea, TextInput, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDots, IconEye, IconHistory, IconPencil, IconPlus, IconStar, IconStarFilled } from "@tabler/icons-react";
import { AttachmentNumber, CTC_ATTACHMENT_TEMPLATES_PATH, CTC_ATTACHMENT_NAME_MAX, CTC_ATTACHMENT_NUMBERS, CTC_ATTACHMENT_TEXT_MAX, CTC_ATTACHMENT_TITLES, CTC_BUILTIN_ATTACHMENT_TEXT, ctcAttachmentTemplateProblem } from "../../../../shared/ctcAttachments";
import { CtcAttachmentTemplate, CtcAttachmentTemplateVersion, CtcCustomer } from "../../types";
import { useLiveCollection } from "./useLive";
import { useCtcCallables } from "./ctc_helpers";
import { CtcAttachmentPreview } from "./ctc_attachment_preview";

/*
 * Back office → Attachment templates: the English / Arabic paragraph of each generated attachment
 * (1-5). Each number has many templates and exactly one default (what new customers start with).
 * Every save is a new version; History shows them and can restore one (as a new version).
 * All writes go through functions-ctc (ctcSaveAttachmentTemplate / ctcUpdateAttachmentTemplate).
 */

type Template = CtcAttachmentTemplate & { id: string };
const when = (ts?: { toDate?: () => Date }) => ts?.toDate?.().toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) ?? "";

/** Active templates (not deleted), every number; for the customer form and this tab. */
export const useAttachmentTemplates = () => {
    const { data, loading } = useLiveCollection<Template>(CTC_ATTACHMENT_TEMPLATES_PATH);
    return { templates: (data ?? []).filter((t) => !t.deleted), loading };
};

type Draft = { id?: string; attachmentNumber: AttachmentNumber; name: string; text_en: string; text_ar: string; makeDefault: boolean; note?: string };

function TemplateForm({ draft, onClose }: { draft: Draft; onClose: () => void }) {
    const { saveAttachmentTemplate } = useCtcCallables();
    const [value, setValue] = useState(draft);
    const isCurrentDefault = !!draft.id && draft.makeDefault;
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(false);
    const problem = ctcAttachmentTemplateProblem(value);
    const counter = (text: string) => `${text.trim().length} / ${CTC_ATTACHMENT_TEXT_MAX}`;

    const save = async () => {
        if (problem) return;
        setSaving(true);
        try {
            const res = await saveAttachmentTemplate(value);
            notifications.show({ color: "green", message: `Saved${res.data.version ? ` as version ${res.data.version}` : ""}` });
            onClose();
        } catch (e) {
            notifications.show({ color: "red", message: (e as Error).message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal opened onClose={() => !saving && onClose()} size="xl" title={`${value.id ? "Edit" : "New"} template · Attachment ${value.attachmentNumber}: ${CTC_ATTACHMENT_TITLES[value.attachmentNumber]}`}>
            <Stack>
                <TextInput label="Name" required maxLength={CTC_ATTACHMENT_NAME_MAX} value={value.name} onChange={(e) => setValue({ ...value, name: e.currentTarget.value })} />
                <Textarea label="English text" required autosize minRows={3} maxLength={CTC_ATTACHMENT_TEXT_MAX}
                    description={`Used when the order language is English · ${counter(value.text_en)}`}
                    value={value.text_en} onChange={(e) => setValue({ ...value, text_en: e.currentTarget.value })} />
                <Textarea label="Arabic text" required autosize minRows={3} maxLength={CTC_ATTACHMENT_TEXT_MAX} dir="rtl"
                    description={`Used when the order language is Arabic · ${counter(value.text_ar)}`}
                    value={value.text_ar} onChange={(e) => setValue({ ...value, text_ar: e.currentTarget.value })} />
                {/* A number always has one default: the current default can only stop being it when another is made default */}
                <Checkbox label={`Default for Attachment ${value.attachmentNumber} (what new customers start with)`} checked={value.makeDefault} disabled={isCurrentDefault}
                    description={isCurrentDefault ? "This is the default. To change it, make another template the default." : undefined}
                    onChange={(e) => setValue({ ...value, makeDefault: e.currentTarget.checked })} />
                {value.id && <Text size="xs" c="dimmed">Saving a change creates a new version. Customers using this template get it from their next PDF.</Text>}
                <Group justify="space-between">
                    <Button variant="default" leftSection={<IconEye size={16} />} onClick={() => setPreview(true)} disabled={!value.text_en.trim() || !value.text_ar.trim()}>Preview</Button>
                    <Group>
                        <Button variant="default" onClick={onClose} disabled={saving}>Cancel</Button>
                        <Tooltip label={problem} disabled={!problem}>
                            <Button onClick={save} loading={saving} disabled={!!problem}>Save</Button>
                        </Tooltip>
                    </Group>
                </Group>
            </Stack>
            <CtcAttachmentPreview opened={preview} onClose={() => setPreview(false)} number={value.attachmentNumber} text={{ en: value.text_en, ar: value.text_ar }} />
        </Modal>
    );
}

function TemplateHistory({ template, onClose, onRestore }: { template: Template; onClose: () => void; onRestore: (v: CtcAttachmentTemplateVersion) => void }) {
    const { data } = useLiveCollection<CtcAttachmentTemplateVersion>(`${CTC_ATTACHMENT_TEMPLATES_PATH}/${template.id}/versions`);
    const versions = [...(data ?? [])].sort((a, b) => b.version - a.version);
    const [preview, setPreview] = useState<CtcAttachmentTemplateVersion | null>(null);
    return (
        <Modal opened onClose={onClose} size="xl" title={`History · ${template.name}`}>
            <Table striped withTableBorder fz="sm">
                <Table.Thead><Table.Tr><Table.Th>Version</Table.Th><Table.Th>Saved</Table.Th><Table.Th>By</Table.Th><Table.Th>Note</Table.Th><Table.Th /></Table.Tr></Table.Thead>
                <Table.Tbody>
                    {versions.map((v) => (
                        <Table.Tr key={v.version}>
                            <Table.Td>v{v.version}{v.version === template.version && <Badge ml={6} size="xs" variant="light">current</Badge>}</Table.Td>
                            <Table.Td>{when(v.savedAt)}</Table.Td>
                            <Table.Td>{v.savedBy?.split("@")[0]}</Table.Td>
                            <Table.Td>{v.note ?? ""}</Table.Td>
                            <Table.Td ta="right">
                                <Group gap={4} justify="flex-end" wrap="nowrap">
                                    <Button size="compact-xs" variant="subtle" onClick={() => setPreview(v)}>Preview</Button>
                                    {v.version !== template.version && <Button size="compact-xs" variant="light" onClick={() => onRestore(v)}>Restore</Button>}
                                </Group>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                    {!versions.length && <Table.Tr><Table.Td colSpan={5}><Text size="sm" c="dimmed">No versions yet.</Text></Table.Td></Table.Tr>}
                </Table.Tbody>
            </Table>
            {preview && <CtcAttachmentPreview opened onClose={() => setPreview(null)} number={template.attachmentNumber} text={{ en: preview.text_en, ar: preview.text_ar }} title={`${template.name} · v${preview.version}`} />}
        </Modal>
    );
}

type CustomerRow = Pick<CtcCustomer, "name_en" | "attachmentTemplateIds" | "deleted"> & { id: string };

function NumberTab({ n, templates, customers }: { n: AttachmentNumber; templates: Template[]; customers: CustomerRow[] }) {
    const { updateAttachmentTemplate } = useCtcCallables();
    const [showRetired, setShowRetired] = useState(false);
    const [editing, setEditing] = useState<Draft | null>(null);
    const [history, setHistory] = useState<Template | null>(null);
    const [preview, setPreview] = useState<Template | null>(null);
    const rows = templates.filter((t) => t.attachmentNumber === n && (showRetired || !t.retired)).sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.name.localeCompare(b.name));
    const hasAny = templates.some((t) => t.attachmentNumber === n);

    const defaultTemplate = templates.find((x) => x.attachmentNumber === n && x.isDefault);
    const act = async (t: Template, action: "make_default" | "retire" | "unretire" | "delete") => {
        if (action === "retire" || action === "delete") {
            // Customers using it are switched to the default by the server; say who before doing it
            const using = customers.filter((c) => !c.deleted && c.attachmentTemplateIds?.[String(n) as "1"] === t.id);
            const names = using.slice(0, 10).map((c) => c.name_en).join(", ") + (using.length > 10 ? ` and ${using.length - 10} more` : "");
            const what = action === "retire" ?
                `Retire "${t.name}"?\n\nIt will no longer be offered for customers. You can un-retire it later.` :
                `Delete "${t.name}"?\n\nIt will be removed for good (its history is kept).`;
            const who = using.length ?
                `\n\n${using.length} customer${using.length === 1 ? " uses" : "s use"} it for Attachment ${n}: ${names}.\nThey will be switched to the default "${defaultTemplate?.name ?? "built-in text"}".` :
                "\n\nNo customer uses it.";
            if (!window.confirm(what + who)) return;
        }
        try {
            const res = await updateAttachmentTemplate({ id: t.id, action });
            const moved = res.data.movedCustomers ? `. ${res.data.movedCustomers} customer${res.data.movedCustomers === 1 ? "" : "s"} switched to "${defaultTemplate?.name ?? "the default"}"` : "";
            notifications.show({ color: "green", message: `${{ make_default: "Default changed", retire: "Retired", unretire: "Un-retired", delete: "Deleted" }[action]}${moved}` });
        } catch (e) {
            notifications.show({ color: "red", message: (e as Error).message });
        }
    };
    const edit = (t: Template) => setEditing({ id: t.id, attachmentNumber: n, name: t.name, text_en: t.text_en, text_ar: t.text_ar, makeDefault: t.isDefault });

    return (
        <Stack>
            <Group justify="space-between">
                <Checkbox label="Show retired templates" checked={showRetired} onChange={(e) => setShowRetired(e.currentTarget.checked)} />
                <Group>
                    {!hasAny && (
                        <Button variant="light" onClick={() => setEditing({ attachmentNumber: n, name: "Default", text_en: CTC_BUILTIN_ATTACHMENT_TEXT[n].en, text_ar: CTC_BUILTIN_ATTACHMENT_TEXT[n].ar, makeDefault: true })}>
                            Create default from current text
                        </Button>
                    )}
                    <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing({ attachmentNumber: n, name: "", text_en: "", text_ar: "", makeDefault: !hasAny })}>Add template</Button>
                </Group>
            </Group>
            <Table striped highlightOnHover withTableBorder>
                <Table.Thead><Table.Tr><Table.Th w={40} /><Table.Th>Name</Table.Th><Table.Th>English</Table.Th><Table.Th>Arabic</Table.Th><Table.Th>Version</Table.Th><Table.Th>Updated</Table.Th><Table.Th /></Table.Tr></Table.Thead>
                <Table.Tbody>
                    {rows.map((t) => (
                        <Table.Tr key={t.id} style={t.retired ? { opacity: 0.5 } : undefined}>
                            <Table.Td>
                                <Tooltip label={t.isDefault ? "Default" : "Make default"}>
                                    <ActionIcon variant="subtle" color="yellow" onClick={() => !t.isDefault && act(t, "make_default")} disabled={t.retired} aria-label="Default">
                                        {t.isDefault ? <IconStarFilled size={16} /> : <IconStar size={16} />}
                                    </ActionIcon>
                                </Tooltip>
                            </Table.Td>
                            <Table.Td fw={500}>{t.name}{t.isDefault && <Badge ml={6} size="xs" color="yellow" variant="light">default</Badge>}{t.retired && <Badge ml={6} size="xs" color="gray" variant="light">retired</Badge>}</Table.Td>
                            <Table.Td maw={260}><Text size="xs" lineClamp={2}>{t.text_en}</Text></Table.Td>
                            <Table.Td maw={260}><Text size="xs" lineClamp={2} dir="rtl">{t.text_ar}</Text></Table.Td>
                            <Table.Td>v{t.version}</Table.Td>
                            <Table.Td><Text size="xs">{when(t.updated)}</Text><Text size="xs" c="dimmed">{t.updatedBy?.split("@")[0]}</Text></Table.Td>
                            <Table.Td ta="right">
                                <Group gap={2} justify="flex-end" wrap="nowrap">
                                    <Tooltip label="Preview"><ActionIcon variant="subtle" onClick={() => setPreview(t)} aria-label="Preview"><IconEye size={16} /></ActionIcon></Tooltip>
                                    <Tooltip label="Edit"><ActionIcon variant="subtle" onClick={() => edit(t)} disabled={t.retired} aria-label="Edit"><IconPencil size={16} /></ActionIcon></Tooltip>
                                    <Tooltip label="History"><ActionIcon variant="subtle" onClick={() => setHistory(t)} aria-label="History"><IconHistory size={16} /></ActionIcon></Tooltip>
                                    <Menu position="bottom-end" withinPortal>
                                        <Menu.Target><ActionIcon variant="subtle" color="gray" aria-label="More"><IconDots size={16} /></ActionIcon></Menu.Target>
                                        <Menu.Dropdown>
                                            {t.retired ?
                                                <Menu.Item onClick={() => act(t, "unretire")}>Un-retire</Menu.Item> :
                                                <Menu.Item disabled={t.isDefault} onClick={() => act(t, "retire")}>Retire{t.isDefault ? " (default)" : ""}</Menu.Item>}
                                            <Menu.Item color="red" disabled={t.isDefault} onClick={() => act(t, "delete")}>Delete{t.isDefault ? " (default)" : ""}</Menu.Item>
                                        </Menu.Dropdown>
                                    </Menu>
                                </Group>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                    {!rows.length && <Table.Tr><Table.Td colSpan={7}><Text size="sm" c="dimmed">No templates yet. PDFs use the current built-in text until one is created.</Text></Table.Td></Table.Tr>}
                </Table.Tbody>
            </Table>
            {editing && <TemplateForm draft={editing} onClose={() => setEditing(null)} />}
            {history && (
                <TemplateHistory template={history} onClose={() => setHistory(null)}
                    onRestore={(v) => { setHistory(null); setEditing({ id: history.id, attachmentNumber: n, name: v.name, text_en: v.text_en, text_ar: v.text_ar, makeDefault: history.isDefault, note: `Restored from v${v.version}` }); }} />
            )}
            {preview && <CtcAttachmentPreview opened onClose={() => setPreview(null)} number={n} text={{ en: preview.text_en, ar: preview.text_ar }} title={`${preview.name} · v${preview.version}`} />}
        </Stack>
    );
}

export default function CtcAttachmentTemplates() {
    const { templates } = useAttachmentTemplates();
    const customers = useLiveCollection<CustomerRow>("ctc_customers").data ?? [];
    return (
        <Tabs defaultValue="1" variant="pills" keepMounted={false}>
            <Tabs.List mb="md">
                {CTC_ATTACHMENT_NUMBERS.map((n) => (
                    <Tooltip key={n} label={CTC_ATTACHMENT_TITLES[n]}>
                        <Tabs.Tab value={String(n)}>Attachment {n}</Tabs.Tab>
                    </Tooltip>
                ))}
            </Tabs.List>
            {CTC_ATTACHMENT_NUMBERS.map((n) => (
                <Tabs.Panel key={n} value={String(n)}>
                    <Text size="sm" c="dimmed" mb="sm">Attachment {n}: {CTC_ATTACHMENT_TITLES[n]}</Text>
                    <NumberTab n={n} templates={templates} customers={customers} />
                </Tabs.Panel>
            ))}
        </Tabs>
    );
}
